/**
 * useRouteGradeSubmissions Hook
 *
 * Custom hook for fetching all grade submissions for a route.
 * Returns the grade distribution for displaying in a bar chart.
 */

import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { V_GRADES, type VGrade, type GradeSubmission } from '@flash/shared';

/**
 * Grade distribution - count of submissions per grade
 */
export type GradeDistribution = Record<VGrade, number>;

/**
 * State interface for route grade submissions query
 */
interface UseRouteGradeSubmissionsState {
  /** All grade submissions for this route */
  submissions: GradeSubmission[];
  /** Grade distribution (count per grade) */
  distribution: GradeDistribution;
  /** Min grade submitted (for display range) */
  minGrade: VGrade | null;
  /** Max grade submitted (for display range) */
  maxGrade: VGrade | null;
  /** Total submission count */
  totalCount: number;
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Initialize empty grade distribution
 */
const initializeDistribution = (): GradeDistribution => {
  const dist: Partial<GradeDistribution> = {};
  for (const grade of V_GRADES) {
    dist[grade] = 0;
  }
  return dist as GradeDistribution;
};

/**
 * Get grade index for comparison
 */
const getGradeIndex = (grade: VGrade): number => {
  return V_GRADES.indexOf(grade);
};

/**
 * Hook to fetch all grade submissions for a specific route
 *
 * @param routeId - The route ID to fetch submissions for
 * @returns Object containing submissions, distribution, and metadata
 *
 * @example
 * ```tsx
 * const {
 *   distribution,
 *   minGrade,
 *   maxGrade,
 *   totalCount,
 *   loading,
 * } = useRouteGradeSubmissions('route-123');
 * ```
 */
export const useRouteGradeSubmissions = (
  routeId: string | undefined
): UseRouteGradeSubmissionsState => {
  const [state, setState] = useState<UseRouteGradeSubmissionsState>({
    submissions: [],
    distribution: initializeDistribution(),
    minGrade: null,
    maxGrade: null,
    totalCount: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!routeId) {
      setState({
        submissions: [],
        distribution: initializeDistribution(),
        minGrade: null,
        maxGrade: null,
        totalCount: 0,
        loading: false,
        error: null,
      });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const submissionsRef = collection(db, 'gradeSubmissions');
    const q = query(submissionsRef, where('routeId', '==', routeId));

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const submissions: GradeSubmission[] = [];
        const distribution = initializeDistribution();
        let minGrade: VGrade | null = null;
        let maxGrade: VGrade | null = null;

        snapshot.forEach((doc) => {
          const data = doc.data();
          const submission: GradeSubmission = {
            id: doc.id,
            odKey: data['odKey'] as string,
            userId: data['userId'] as string,
            routeId: data['routeId'] as string,
            grade: data['grade'] as VGrade,
            createdAt: data['createdAt'],
            updatedAt: data['updatedAt'],
          };
          submissions.push(submission);

          // Update distribution
          const grade = submission.grade;
          if (V_GRADES.includes(grade)) {
            distribution[grade]++;

            // Track min/max grades
            if (minGrade === null || getGradeIndex(grade) < getGradeIndex(minGrade)) {
              minGrade = grade;
            }
            if (maxGrade === null || getGradeIndex(grade) > getGradeIndex(maxGrade)) {
              maxGrade = grade;
            }
          }
        });

        setState({
          submissions,
          distribution,
          minGrade,
          maxGrade,
          totalCount: submissions.length,
          loading: false,
          error: null,
        });
      },
      (err) => {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch grade submissions';
        console.error('Error fetching route grade submissions:', err);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [routeId]);

  return state;
};

export default useRouteGradeSubmissions;
